import React from "react";
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Upload } from "antd";
import { GiftOutlined, IdcardOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import { COMMERCIAL_ENTITY_RCCM_MAX_LENGTH, COMMERCIAL_ENTITY_RCCM_LENGTH_MESSAGE } from "../utils/entityUtils";
import useEntityModalTheme from "./useEntityModalTheme";
import "./styles.css";

const amountProps = {
  min: 0,
  addonAfter: "CFA",
  formatter: value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
  parser: value => value.replace(/\$\s?|(,*)/g, ""),
};
const required = message => [{ required: true, message }];

// Présentation partagée ; les parents gardent les valeurs, validations métier et écritures.
export default function EntityFormModal({
  open, entityType, editing, form, onSubmit, onCancel, confirmLoading,
  warehouses, warehouseId, warehouseResource, entityResource, onPreview,
}) {
  const customer = entityType === "customer";
  const noun = customer ? "client" : "fournisseur";
  const style = useEntityModalTheme();
  const selectedWarehouseId = Form.useWatch("warehouse_id", form);
  const warehouseMissing = warehouseResource.status === "verified" && selectedWarehouseId &&
    !warehouses.some(warehouse => String(warehouse.id) === String(selectedWarehouseId));
  const suspended = warehouseMissing || warehouseResource.status !== "verified" || (editing && entityResource.status !== "verified");
  return (
    <Modal open={open} onCancel={onCancel} centered width={1180}
      className="entity-modal entity-form-modal" style={style}
      title={<div className="entity-form-title"><h2>{editing ? `Modifier le ${noun}` : `Nouveau ${noun}`}</h2>
        <p>Renseignez les coordonnées et les conditions commerciales.</p></div>}
      footer={<div className="entity-form-footer"><span><span className="entity-required">*</span> Champs obligatoires</span>
        <Space><Button onClick={onCancel}>Annuler</Button>
          <Button type="primary" onClick={onSubmit} loading={confirmLoading} disabled={Boolean(suspended)}>
            {editing ? "Enregistrer" : `Créer le ${noun}`}
          </Button></Space></div>}>
      <BusinessResourceNotice resource={warehouseResource} label="les établissements" />
      {editing && <BusinessResourceNotice resource={entityResource} label={`la liste des ${noun}s`} />}
      <Form form={form} layout="vertical" size="large" className="entity-form"
        requiredMark={(label, { required: isRequired }) => <>{label}{isRequired && <span className="entity-required" aria-hidden> *</span>}</>}>
        <div className="entity-form-context">
          <Form.Item name="warehouse_id" label="Établissement" initialValue={warehouseId}
            rules={[...required("Veuillez sélectionner un établissement"), { validator: (_, value) =>
              warehouseResource.status === "verified" && value && !warehouses.some(warehouse => String(warehouse.id) === String(value))
                ? Promise.reject(new Error("Cet établissement n’est plus disponible. Sélectionnez un autre établissement.")) : Promise.resolve() }]}
            tooltip={`L'établissement principal auquel ce ${noun} est rattaché.`}>
            <Select placeholder="Sélectionner un établissement" showSearch optionFilterProp="label"
              options={warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name }))} />
          </Form.Item>
          <Form.Item name="status" label="Statut" rules={required("Veuillez sélectionner un statut")}>
            <Select placeholder="Sélectionner un statut" options={[{ value: "enabled", label: "Activé" }, { value: "disabled", label: "Désactivé" }]} />
          </Form.Item>
        </div>
        {warehouseMissing && <Alert showIcon type="warning" message="Cet établissement n’est plus disponible. Sélectionnez un autre établissement." />}
        <div className="entity-form-columns">
          <section aria-label="Identité et coordonnées">
            <h3><UserOutlined aria-hidden /> Identité et coordonnées</h3>
            <Form.Item name="name" label="Nom ou raison sociale" rules={required("Veuillez saisir le nom")}>
              <Input placeholder="Nom ou Raison sociale" />
            </Form.Item>
            <div className="entity-form-pair">
              <Form.Item name="email" label="Email (optionnel)" rules={[{ type: "email", message: "Email invalide" }]}>
                <Input placeholder="Email" />
              </Form.Item>
              <Form.Item name="phone" label="Téléphone"><Input placeholder="Téléphone" /></Form.Item>
            </div>
            <Form.Item name="address" label="Adresse" rules={required("Veuillez saisir l'adresse")}>
              <Input placeholder="Adresse" />
            </Form.Item>
            <Form.Item name="shipping_address" label="Adresse de livraison"><Input placeholder="Si différente" /></Form.Item>
            <Form.Item name="profile_image" label={`Logo du ${noun} (optionnel)`} valuePropName="fileList"
              getValueFromEvent={event => Array.isArray(event) ? event : event?.fileList}
              extra="Affiché dans les listes et documents." className="entity-form-logo">
              <Upload name="profile_image" listType="picture" onPreview={onPreview} beforeUpload={() => false} maxCount={1}>
                <Button icon={<UploadOutlined aria-hidden />}>Ajouter un logo</Button>
              </Upload>
            </Form.Item>
          </section>
          <section aria-label="Conditions commerciales">
            <h3><IdcardOutlined aria-hidden /> Conditions commerciales</h3>
            <div className="entity-form-pair">
              <Form.Item name={["user_details", "rccm"]} label="Numéro RCCM (optionnel)"
                rules={[{ max: COMMERCIAL_ENTITY_RCCM_MAX_LENGTH, message: COMMERCIAL_ENTITY_RCCM_LENGTH_MESSAGE }]}>
                <Input placeholder="RCCM" />
              </Form.Item>
              <Form.Item name={["user_details", "ifu"]} label="Numéro IFU (optionnel)"><Input placeholder="IFU" /></Form.Item>
            </div>
            <div className="entity-form-pair">
              <Form.Item name={["user_details", "credit_period"]} label="Période de crédit" rules={required("Veuillez saisir la période de crédit")}>
                <InputNumber min={0} addonAfter="jours" placeholder="Ex: 30" />
              </Form.Item>
              <Form.Item name={["user_details", "credit_limit"]} label="Limite de crédit" rules={required("Veuillez saisir la limite de crédit")}>
                <InputNumber {...amountProps} placeholder={customer ? "Ex: 500000" : "Ex: 1000000"} />
              </Form.Item>
            </div>
            <div className="entity-form-pair">
              <Form.Item name={["user_details", "opening_balance"]} label="Solde d’ouverture" rules={required("Veuillez saisir le solde d'ouverture")}>
                <InputNumber {...amountProps} placeholder="Montant initial" />
              </Form.Item>
              <Form.Item name={["user_details", "opening_balance_type"]} label="Type de solde" rules={required("Veuillez sélectionner le type de solde")}>
                <Select placeholder="Sélectionner le type" options={customer
                  ? [{ value: "receive", label: "À recevoir (Créance)" }, { value: "pay", label: "À payer (Dette)" }]
                  : [{ value: "pay", label: "À payer (Dette)" }, { value: "receive", label: "À recevoir (Créance)" }]} />
              </Form.Item>
            </div>
            <p className="entity-form-help">Montant existant avant la création de la fiche.</p>
            {customer && <div className="entity-form-loyalty">
              <h3><GiftOutlined aria-hidden /> Fidélité</h3>
              <div className="entity-form-pair">
                <Form.Item name={["user_details", "loyalty_status"]} label="Statut fidélité" rules={required("Veuillez sélectionner le statut fidélité")}>
                  <Select placeholder="Sélectionner un statut" options={[{ value: "active", label: "Active" }, { value: "disabled", label: "Désactivée" }, { value: "revoked", label: "Révoquée" }]} />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>{({ getFieldValue }) => (
                  <Form.Item name={["user_details", "loyalty_status_reason"]} label="Motif (optionnel)">
                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="Si désactivée ou révoquée"
                      disabled={(getFieldValue(["user_details", "loyalty_status"]) || "active") === "active"} />
                  </Form.Item>
                )}</Form.Item>
              </div>
            </div>}
          </section>
        </div>
      </Form>
    </Modal>
  );
}
